import atnlSelect from '@components/atnl-select/script';

/**
 * Class to manage taxonomy and filter URL manipulation for filtering UI components.
 */
export default class FilterHelpers {

	/**
	 * Retrieves taxonomy term(s) from URL parameters or fallback global defaults.
	 *
	 * @param {Object} urlParams - Object representing parsed URL parameters.
	 * @param {string} tax - The taxonomy key to retrieve.
	 * @returns {Array|null} An array of term strings or null if none found.
	 */
	static getTaxTerm(urlParams, tax) {
		let terms = urlParams[tax] || (
			window.atnlbs_default?.query_vars?.taxonomy === tax &&
			window.atnlbs_default?.query_vars?.term
		);

		return terms ? terms.split(',') : null;
	}

	/**
	 * Constructs a new URL based on current filters applied.
	 *
	 * @param {string} currentUrl - The base URL from which to start.
	 * @param {Object} taxFilters - Key-value pairs of taxonomy filters.
	 * @param {Object} nonTaxFilters - Key-value pairs of non-taxonomy filters.
	 * @returns {string} A formatted URL string with the appropriate filter parameters.
	 */
	static buildFilterUrl(currentUrl, taxFilters = {}, nonTaxFilters = {}) {
		const url = new URL(currentUrl);
		const baseUrl = url.pathname.split('/f/')[0].replace(/\/$/, '');

		let pathTax = null;
		// Choose a single taxonomy to embed in the URL path, if applicable
		for (let tax in taxFilters) {
			if (taxFilters[tax] && (typeof taxFilters[tax] === 'string' || taxFilters[tax].length < 2)) {
				pathTax = [tax, taxFilters[tax]];
				break;
			}
		}

		const query = new URLSearchParams();

		// Add taxonomy filters to query string, excluding the one used in path
		for (let tax in taxFilters) {
			if (taxFilters[tax] && (!pathTax || tax !== pathTax[0])) {
				query.set(tax, taxFilters[tax]);
			}
		}

		// Add non-taxonomy filters to query string
		for (let key in nonTaxFilters) {
			if (nonTaxFilters[key]) {
				query.set(`${key}`, nonTaxFilters[key]);
			}
		}

		const path = pathTax ? `/f/${encodeURIComponent(pathTax[0])}/${encodeURIComponent(pathTax[1])}` : '';
		const queryString = query.toString() ? `?${query.toString()}` : '';

		return `${url.origin}${baseUrl}${path}/${queryString}`;
	}

	/**
	 * Automatically applies filters to the filter form based on provided URL parameters.
	 *
	 * @param {HTMLElement} filterForm - The DOM element representing the filter form.
	 * @param {Object} urlParams - Parsed URL parameters.
	 * @param {Array<string>} taxonomies - Array of taxonomy keys to apply.
	 * @param {Array<string>} nonTaxFilters - Array of non-taxonomy keys to apply.
	 */
	static applyFiltersToAtnlSelects(filterForm, urlParams, taxonomies = [], nonTaxFilters = []) {
		let checked = 0;

		// Apply taxonomy filters
		taxonomies.forEach(tax => {
			const terms = this.getTaxTerm(urlParams, tax);
			terms?.forEach(term => {
				if (term) {
					const select = filterForm.querySelector(`[data-for="${tax}"] input[value="${term}"]`);
					if (select) {
						atnlSelect.optionChecker(select, true, false);
						checked++;
					}
				}
			});
		});

		// Apply non-taxonomy filters
		nonTaxFilters.forEach(filter => {
			const val = urlParams[filter];
			if (val) {
				const select = filterForm.querySelector(`[data-for="${filter}"] input[value="${val}"]`);
				if (select) {
					atnlSelect.optionChecker(select, true, false);
					checked++;
				}
			}
		});

		// Scroll to form if filters were applied
		if (checked > 0) {
			filterForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}
}

